import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { useSelector } from "react-redux";
import { ArrowDown, ArrowUp } from "lucide-react";
import ExportIcon from "../../icons/ExportIcon";
import ChartCard from "../components/ChartCard";
import BarChart from "../components/charts/BarChart";
import InputInlineWidget from "../components/InputInlineWidget";
import { ChartTypes } from "../../constants/action-types";
import { colors as defaultColors } from "../../board/components/TaskDetail/ColorPicker";
import {
  datetimeFields,
  getMeasureByLabel,
  getViewByLabel,
  handleDataBarChart,
} from "../DataUtils";
import { useAggregatedData } from "../hooks/useReportData";
import ChartLoading from "../components/ChartLoading";

const ChartBarWidget = ({
  projectId,
  widget,
  className,
  onClickUp,
  onClickDown,
  onClickExport,
  onClickSetting,
  onClickDelete,
  onUpdateTitle,
  onUpdateSubtitle,
}) => {
  const { t } = useTranslation();

  const ruleReducer = useSelector((state: any) => state.ruleReducer);

  const objectType = widget.data?.objectType || "SALE";
  const title = widget.data?.title;
  const subtitle = widget.data?.subtitle;
  const viewBy = widget.data?.nameKey || "bucketId";
  const segmentBy = widget.data?.segmentBy || "None";
  const measureBy = widget.data?.valueKey || "dealCount";
  const dateUnit = widget.data?.dateUnit || "day";
  const filters = widget.data?.filters || {};
  const colors = widget.data?.colors || [];
  const barColors = colors.length > 0 ? colors : defaultColors;
  const groups = ruleReducer.get(viewBy) || fromJS([]);
  const segments = ruleReducer.get(segmentBy) || fromJS([{ label: "None", value: "None" }]);

  const { data: dataAggregated, isLoading: isLoadingAggregated } = useAggregatedData(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    filters,
    objectType,
    true
  );

  return (
    <ChartCard
      title={
        <InputInlineWidget
          placeholder={t("e.g Deal Performance")}
          value={title}
          className="text-sm sm:text-sm/6 md:text-base font-medium text-gray-600 placeholder:text-gray-400"
          onFinish={onUpdateTitle}
        />
      }
      subtitle={
        subtitle ? (
          <InputInlineWidget
            placeholder={t("e.g Last year")}
            value={subtitle}
            className="text-sm text-gray-600 placeholder:text-gray-400"
            onFinish={onUpdateSubtitle}
          />
        ) : null
      }
      className={className}
      isLoading={isLoadingAggregated}
      // searchPlaceholder="Search"
      actions={[
        {
          label: t("Up"),
          content: <ArrowUp className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickUp(event),
        },
        {
          label: t("Down"),
          content: <ArrowDown className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickDown(event),
        },
        {
          label: t("Export"),
          content: <ExportIcon />,
          onClick: () => onClickExport(filters),
        },
      ]}
      onSetting={onClickSetting}
      onDelete={onClickDelete}
    >
      {dataAggregated && !isLoadingAggregated ? (
        <BarChart
          data={handleDataBarChart(dataAggregated, viewBy, measureBy, dateUnit, groups, segments)}
          stacked={true}
          barSize={30}
          barColors={barColors}
          layout={widget?.data?.chartType === ChartTypes.column ? "horizontal" : "vertical"}
          isByDate={datetimeFields.includes(viewBy)}
          labelTooltip={getViewByLabel(objectType, viewBy).label}
          segmentLabelTooltip={getMeasureByLabel(objectType, measureBy)?.label}
        />
      ) : (
        <ChartLoading />
      )}
    </ChartCard>
  );
};

export default ChartBarWidget;
